import React, { useState, useEffect } from 'react';
import { withStyles } from '@mui/styles';
import { observer } from 'mobx-react-lite';
import { withRouter } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import LoaderBounce from 'components/loaders/LoaderBounce';
import CandidatePhotoInfo from 'components/candidate_cards/CandidateCardWrapper/CandidatePhotoInfo';
import CandidateStatusInfo from 'components/candidate_cards/CandidateCardWrapper/CandidateStatusInfo';
import CommentsInfo from 'components/candidate_cards/CandidateCardWrapper/CommentsInfo';
import TagsInfo from 'components/candidate_cards/CandidateCardWrapper/TagsInfo';
import CardScoreBlock from 'components/candidate_cards/CandidateCardWrapper/CardScoreBlock';
import GradeBlock from 'components/candidate_cards/CandidateCardWrapper/GradeBlock';
import FraudDetectedBanner from 'components/candidate_cards/CandidateCardWrapper/WarningBanner/FraudDetectedBanner';
import CheatDetectedBanner from 'components/candidate_cards/CandidateCardWrapper/WarningBanner/CheatDetectedBanner';
import NotCompletedBlock from 'components/candidate_cards/CandidateCardWrapper/NotCompletedBlock';
import SatisfactionBlock from 'components/candidate_cards/CandidateCardWrapper/SatisfactionBlock';
import StatusHistory from 'components/candidate_cards/CandidateCardWrapper/StatusHistory';
import CardPerformanceBlock from 'components/candidate_cards/CandidateCardWrapper/CardPerformanceBlock';
import { getCandidateProfile } from 'requests/APIrequests';
import SummaryBlock from 'components/candidate_cards/CandidateCardWrapper/SummaryBlock';
import CandidatesListPublicCard from '../CandidatesListPublicCard';

const styles = theme => ({
    detailInfoItem: {
        display: 'block',
        marginRight: 20,
        marginBottom: '0 !important',
        whiteSpace: 'nowrap'
    },
    userInfo: {
        display: 'flex',
        borderTop: `2px solid ${theme.palette.grey[200]}`,
        borderBottom: `2px solid ${theme.palette.grey[200]}`,
        '& > .candidateList': {
            width: '30%'
        },
        '& > .statusInfo': {
            width: '40%',
            '& .photoInfo': {
                padding: '0 20px 20px 0'
            }
        },
        '& > .performance': {
            width: '30%'
        }
    }
});

const CandidateContent = observer(({
    getUserInfo, company, isLoading, classes,
    user, opportunitySlug, audition, audition: { uuid, name },
    loadCandidatePosts, propPublic, authorizeGuest, candidatesProp, gradeableCard,
    addCandidateComment, loadGradeQuestion, gradeFunction,
    getAuditionQuestionResults, hideCandidateDetails, isLoadingAudition,
    getAuditionScreeningResults, multiProps
}) => {
    const [showHireBanner, setShowHireBanner] = useState(false);
    const [profile, setProfile] = useState({});

    useEffect(() => {
        setShowHireBanner(false);
    }, [user && user.id]);

    const dataForEvents = {
        candidateId: user ? user.candidateUuid : null,
        ttId: uuid,
        ttName: name,
        context: `candidate multiple ${gradeableCard ? 'agency' : 'public'} card`
    };


    const getUserProfile = (userUuid, callback = () => {}) => {
        getCandidateProfile(userUuid, Boolean(propPublic))
            .then((response) => {
                const { success, data } = response;
                if (success) {
                    setProfile(data);
                }
            })
            .finally(callback);
    };

    if (isLoading) {
        return (
            <div className="u-txt--center u-pdn--tx10 u-pdn--bx10">
                <LoaderBounce />
            </div>
        );
    }

    if (!user) return null;
    const { permissions } = audition;

    return (
        <>
            <Typography variant="h4" style={{ whiteSpace: 'pre-wrap', marginBottom: 15 }}>
                {audition.name}
            </Typography>
            {
                isLoadingAudition && (
                    <div className="u-txt--center u-pdn--tx10 u-pdn--bx10 u-pos--overlay">
                        <LoaderBounce />
                    </div>
                )
            }
            <div className={classes.userInfo}>
                <div className="candidateList">
                    <div className={classes.leftPanel}>
                        <CandidatesListPublicCard
                            {...multiProps}
                            hideCandidateDetails={hideCandidateDetails}
                        />
                    </div>
                </div>
                <div className="statusInfo">
                    <CandidateStatusInfo
                        dataForEvents={dataForEvents}
                        user={user}
                        company={company}
                        permissions={permissions}
                        showHireBanner={showHireBanner}
                        setShowHireBanner={setShowHireBanner}
                        hideCandidateDetails={hideCandidateDetails}
                        profile={profile}
                        getUserProfile={getUserProfile}
                        propPublic={propPublic}
                        audition={audition}
                        HeaderComp={() => (
                            <CandidatePhotoInfo
                                {...{
                                    user,
                                    audition,
                                    company,
                                    propPublic,
                                    getUserInfo,
                                    opportunitySlug,
                                    showHireBanner,
                                    setShowHireBanner,
                                    hideCandidateDetails,
                                    getUserProfile
                                }}
                            />
                        )}
                    />
                </div>
                <div className="performanceInfo">
                    <CardPerformanceBlock
                        candidate={user}
                        gradeableCard={gradeableCard}
                    />
                </div>
            </div>
            <FraudDetectedBanner
                candidate={user}
                audition={audition}
                getUserInfo={getUserInfo}
            />
            <CheatDetectedBanner
                candidate={user}
                company={company}
                audition={audition}
                getUserInfo={getUserInfo}
            />
            {
                user.candidateAiSummary && !user.archived && user.score && (
                    <SummaryBlock user={user} />
                )
            }
            {
                user.completed ? (
                    <>
                        {
                            !(gradeableCard && !user.score) && (
                                <CardScoreBlock
                                    gradeableCard={gradeableCard}
                                    propPublic={propPublic}
                                    candidate={user}
                                />
                            )
                        }
                    </>
                ) : <NotCompletedBlock audition={audition} />
            }
            {
                (user.completed || user.screeningQuestions) && (
                    <div className="u-mrg--tx5">
                        <GradeBlock
                            audition={audition}
                            candidate={user}
                            propPublic={propPublic}
                            gradeFunction={gradeFunction}
                            updateUserAuditionAndBox={getUserInfo}
                            loadGradeQuestion={loadGradeQuestion}
                            gradeableCard={gradeableCard}
                            authorizeGuest={authorizeGuest}
                            getAuditionResults={getAuditionQuestionResults}
                            getAuditionScreeningResults={getAuditionScreeningResults}
                        />
                    </div>
                )
            }
            <TagsInfo
                candidate={user}
                audition={audition}
                propPublic={propPublic}
            />
            {
                !candidatesProp && (gradeableCard || !propPublic) && (
                    <CommentsInfo
                        user={user}
                        audition={audition}
                        opportunitySlug={opportunitySlug}
                        loadCandidatePosts={loadCandidatePosts}
                        propPublic={propPublic}
                        authorizeGuest={authorizeGuest}
                        addCandidateComment={addCandidateComment}
                    />
                )
            }
            {
                user.completed && (
                    <SatisfactionBlock
                        audition={audition}
                        candidate={user}
                        propPublic={propPublic}
                    />
                )
            }
            {
                user.statusHistory && (
                    <div className="u-mrg--tx5">
                        <StatusHistory statusHistory={user.statusHistory} />
                    </div>
                )
            }
        </>
    );
});

export default withStyles(styles)(withRouter(CandidateContent));
